import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FlaskConical, ArrowRight, Rocket, Brain, Shield, Truck, HardHat, 
  Briefcase, Users, Target, Lightbulb, Wrench, Building2, 
  ShieldCheck, Cog, GraduationCap, Megaphone, Bot, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function ProLab() {
  const labCategories = [
    {
      category: "Enterprise & Business Labs",
      color: "#1E3A8A",
      labs: [
        { id: "ai-entrepreneurship", icon: Rocket, name: "AI Entrepreneurship Lab", description: "Build and launch AI-powered business ventures.", status: "Active" },
        { id: "project-management", icon: Target, name: "Project Management AI Lab", description: "Master AI-enhanced project planning and execution.", status: "Active" },
        { id: "sales-marketing", icon: Megaphone, name: "Sales & Marketing AI Lab", description: "Leverage AI for customer acquisition and growth.", status: "Active" },
        { id: "automation", icon: Bot, name: "Automation AI Lab", description: "Design and implement intelligent automation systems.", status: "Coming Soon" }
      ]
    },
    {
      category: "Leadership & Professional Skills Labs",
      color: "#2563EB",
      labs: [
        { id: "leadership", icon: Users, name: "Leadership AI Lab", description: "Develop AI-augmented leadership capabilities.", status: "Active" },
        { id: "communication", icon: Lightbulb, name: "Communication AI Lab", description: "Master AI-enhanced communication strategies.", status: "Active" }
      ]
    },
    {
      category: "Quality, Safety & Operations Labs",
      color: "#3B82F6",
      labs: [
        { id: "tqm", icon: ShieldCheck, name: "Total Quality Management (TQM) AI Lab", description: "Implement AI-driven quality management systems.", status: "Active" },
        { id: "quality-auditing", icon: Target, name: "Quality Auditing AI Lab", description: "Conduct AI-enhanced quality audits and assessments.", status: "Active" },
        { id: "osha-safety", icon: Shield, name: "OSHA & Workplace Safety AI Lab", description: "Apply AI to workplace safety compliance and prevention.", status: "Active" },
        { id: "safety-management", icon: ShieldCheck, name: "Safety Management AI Lab", description: "Build comprehensive AI-powered safety programs.", status: "Coming Soon" }
      ]
    },
    {
      category: "Industry & Infrastructure Labs",
      color: "#60A5FA",
      labs: [
        { id: "logistics", icon: Truck, name: "Logistics AI Lab", description: "Optimize supply chain and logistics operations.", status: "Active" },
        { id: "construction", icon: HardHat, name: "Construction AI Lab", description: "Apply AI to construction planning and management.", status: "Active" },
        { id: "electrician", icon: Wrench, name: "Electrician AI Lab", description: "AI applications for electrical trade professionals.", status: "Coming Soon" }
      ]
    },
    {
      category: "Security & Protective Services Labs",
      color: "#991B1B",
      labs: [
        { id: "security-level-2", icon: Shield, name: "Security Officer Level II AI Lab", description: "Foundation security operations with AI enhancement.", status: "Active" },
        { id: "security-level-3", icon: Shield, name: "Security Officer Level III AI Lab", description: "Advanced security management and AI integration.", status: "Active" },
        { id: "security-level-4", icon: Shield, name: "Security Officer Level IV AI Lab", description: "Executive protection and strategic security AI.", status: "Coming Soon" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="prolab-page">
      {/* Hero */}
      <section className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/juswork" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to JusWork
            </Link>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-6">
                  <FlaskConical className="w-4 h-4" />
                  Applied AI Learning
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
                  Zodiac ProLab™
                </h1>
                <p className="text-xl text-white/80 mb-2">
                  Applied AI Workforce Labs
                </p>
                <p className="text-lg text-white/70 leading-relaxed mb-8">
                  A mission-based applied AI lab ecosystem where learners develop practical capabilities 
                  across business, leadership, operations, safety, quality, construction, logistics, and industry domains.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-white/80">
                    <FlaskConical className="w-5 h-5" />
                    <span>15+ Applied Labs</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80">
                    <Target className="w-5 h-5" />
                    <span>Mission-Based</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80">
                    <Brain className="w-5 h-5" />
                    <span>AI-Powered</span>
                  </div>
                </div>
                <div className="mt-6">
                  <Link to="/juswork/prolab/dashboard">
                    <Button className="bg-white text-[#1E3A8A] hover:bg-white/90 gap-2">
                      <BarChart3 className="w-4 h-4" />
                      My Learning Dashboard
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block">
                <Card className="bg-white/10 border-white/20">
                  <CardContent className="p-8 text-center">
                    <div className="w-24 h-24 rounded-2xl bg-white/20 mx-auto mb-6 flex items-center justify-center">
                      <FlaskConical className="w-12 h-12 text-white" />
                    </div>
                    <p className="text-3xl font-bold mb-2">15+</p>
                    <p className="text-white/70">Applied AI Labs</p>
                    <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-2xl font-bold">5</p>
                        <p className="text-xs text-white/60">Categories</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">100+</p>
                        <p className="text-xs text-white/60">Missions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lab Directory */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Lab Directory
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Explore our applied AI labs organized by industry and functional area.
            </p>
          </motion.div>

          <div className="space-y-12">
            {labCategories.map((category, catIndex) => (
              <motion.div
                key={catIndex}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: catIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <span 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  ></span>
                  <h3 className="font-heading text-xl font-semibold" style={{ color: '#991B1B' }}>
                    {category.category}
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {category.labs.map((lab, labIndex) => (
                    <Card 
                      key={labIndex}
                      className="border-[#E5E7EB] bg-white hover:shadow-lg transition-all group cursor-pointer"
                      data-testid={`lab-card-${lab.id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${category.color}15` }}
                          >
                            <lab.icon className="w-5 h-5" style={{ color: category.color }} />
                          </div>
                          <Badge 
                            variant="outline"
                            className={lab.status === 'Active' 
                              ? 'bg-[#1E3A8A]/10 text-[#1E3A8A] border-[#1E3A8A]/20' 
                              : 'bg-[#E5E7EB] text-[#4B5563] border-[#E5E7EB]'
                            }
                          >
                            {lab.status}
                          </Badge>
                        </div>
                        <h4 className="font-heading text-sm font-semibold mb-2 leading-tight" style={{ color: '#991B1B' }}>
                          {lab.name}
                        </h4>
                        <p className="text-xs text-[#1E3A8A] mb-4 leading-relaxed">
                          {lab.description}
                        </p>
                        <Link to={lab.status === 'Active' ? `/juswork/prolab/${lab.id}` : '#'}>
                          <Button 
                            size="sm" 
                            className="w-full gap-2 text-white"
                            style={{ backgroundColor: lab.status === 'Active' ? category.color : '#9CA3AF' }}
                            disabled={lab.status !== 'Active'}
                            data-testid={`enter-lab-btn-${lab.id}`}
                          >
                            {lab.status === 'Active' ? 'Enter Lab' : 'Coming Soon'}
                            {lab.status === 'Active' && <ArrowRight className="w-3 h-3" />}
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lab Features */}
      <section className="py-16 lg:py-24 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              What's Inside Each Lab
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Every ProLab provides a structured learning experience with practical AI applications.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { num: "01", title: "Lab Overview", desc: "Purpose and real-world applications explained." },
              { num: "02", title: "Missions", desc: "Step-by-step learning tasks with AI prompts." },
              { num: "03", title: "AI Toolkit", desc: "Specialized AI assistants and prompt tools." },
              { num: "04", title: "Guided Practice", desc: "Scenario-based exercises for skill building." },
              { num: "05", title: "Real-World Application", desc: "Apply skills to actual work projects." },
              { num: "06", title: "Assessment", desc: "Knowledge checks and scenario questions." },
              { num: "07", title: "Portfolio Outputs", desc: "Artifacts: plans, reports, checklists." },
              { num: "08", title: "Lab Dashboard", desc: "Progress tracking and recommendations." }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB]">
                  <CardContent className="p-5">
                    <span className="font-mono text-2xl font-bold text-[#E5E7EB]">{item.num}</span>
                    <h4 className="font-heading text-base font-semibold mt-2 mb-2" style={{ color: '#991B1B' }}>
                      {item.title}
                    </h4>
                    <p className="text-xs text-[#1E3A8A]">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1E3A8A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Ready to Start Learning?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Enroll in a ProLab and begin developing practical AI workforce capabilities today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/admissions">
              <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-12 px-8">
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/programs">
              <Button size="lg" variant="outline" className="h-12 px-8 border-white/30 text-white hover:bg-white/10">
                View Programs
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
